import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, combineLatest, forkJoin, of, from } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthFacade } from '../../store/auth/auth.facade';
import {
  setHasBothPortalModes,
  setIsICADDefault,
} from '../../store/projects/projects.actions';
import { ProjectsFacade } from '../../store/projects/projects.facade';
import { MapLayer } from '../../store/projects/projects.types';
import { Camada, LayerRepresentation } from '../../types/camada.types';
import { Projeto, TIPO_PROJETO } from '../../types/projeto.types';
import { Usuario, UsuarioProjetoPermissao } from '../../types/usuario.types';
import { LayersService } from '../layers/layers.service';
import { IcadService } from './../../../modules/map/services/icad.service';
import { hydrateProjectsCamadas } from '../../utils/projetos';

type Hierarchy = any;

const CITIES_WITHOUT_APPLICATION_HEADER = new Set(['goiania']);

export enum FieldTypeFrontInt {
  TEXT = 1,
  INT = 2,
  NUM = 3,
  M_SELECT = 4,
  SELECT = 5,
  BOOLEAN = 6,
}
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private authFacade: AuthFacade,
    private projectsFacade: ProjectsFacade,
    private router: Router,
    private layersService: LayersService,
    private icadService: IcadService
  ) {}

  getPublicToken(subdomain: string) {
    // TODO ICAD
    const envMunicipio =
      typeof (environment as any).municipio === 'string'
        ? ((environment as any).municipio as string).trim()
        : '';
    const municipio = envMunicipio || subdomain;

    let email = `leitor_${municipio}@vm2info.com`;
    if (municipio === 'rincao') {
      email = 'leitor_rincao@webgeo.io';
    } else if (!environment.production) {
      // email = `leitor_${municipio}@vm2geo.com.br`;
      email = `leitor_${municipio}@vm2info.com`;
      if (municipio === 'new_test' || municipio === 'ouv_test') {
        email = `leitor_${municipio}@vm2geo.com.br`;
      }
    }

    return this.http.get(`${environment.apiUrl}ouv/?q=${email}`, {
      headers: {
        'no-token': '',
      },
    });
  }

  login({ email, password: senha }) {
    return this.http.post(
      `${environment.apiUrl}login`,
      { email, senha },
      {
        headers: {
          'no-token': '',
          'X-PORTAL-LOGIN': '',
        },
      }
    );
  }

  getUserPermission() {
    const user$ = this.authFacade.user$;
    const pvgeoId$ = this.projectsFacade.pvgeoId$;
    const ouvCollabId$ = this.projectsFacade.ouvCollabId$;
    const city$ = this.projectsFacade.city$.pipe(
      map((city) => city ?? undefined)
    );

    return combineLatest([user$, pvgeoId$, ouvCollabId$, city$]).pipe(
      map(([user, pvgeoId, ouvCollabId, city]) => {
        if (!user || !pvgeoId || !ouvCollabId) {
          return;
        }

        const pvgeoPermission = this.getUserPvgeoPermission(
          user,
          pvgeoId,
          city
        );
        const ouvCollabPermission = this.getUserOuvCollabPermission(
          user,
          ouvCollabId,
          city
        );

        return forkJoin([pvgeoPermission, ouvCollabPermission]);
      }),
      switchMap((response$) => {
        if (!response$) {
          return of(false);
        }
        return response$.pipe(
          map(
            ([pvgeoP, ouvCollabP]: [
              UsuarioProjetoPermissao,
              UsuarioProjetoPermissao
            ]) => {
              const pvgeo =
                pvgeoP.permiteCadastrarGeometria &&
                pvgeoP.permiteCadastrarValores;
              const ouvCollab =
                ouvCollabP.permiteCadastrarGeometria &&
                ouvCollabP.permiteCadastrarValores;
              return {
                pvgeo,
                ouvCollab,
              };
            }
          )
        );
      })
    );
  }

  getProjects(): Observable<Projeto[]> {
    const httpOptions = {
      params: new HttpParams().set('ativo', 'TODOS'),
    };

    return this.http
      .get<Projeto[]>(`${environment.apiUrl}projetos/listar`, httpOptions)
      .pipe(
        switchMap((projects) => {
          if (!projects?.length) {
            return of([]);
          }

          const hydrated = [...projects];
          return from(hydrateProjectsCamadas(hydrated));
        })
      );
  }

  getProjectsByType(tipoProjeto: string, includeLayers = false) {
    let params = new HttpParams()
      .set('ativo', 'TODOS')
      .set('tipoProjeto', tipoProjeto)
      .set('noForms', 'true')
      .set('noExtras', 'true');

    if (!includeLayers) {
      params = params.set('noLayers', 'true');
    }

    return this.http
      .get<Projeto[]>(`${environment.apiUrl}projetos/listar`, { params })
      .pipe(
        switchMap((projects) => {
          if (!includeLayers || !projects?.length) {
            return of(projects || []);
          }

          return from(hydrateProjectsCamadas([...projects]));
        })
      );
  }

  mapProjectsToCamadas(projects: Projeto[]): Camada[] {
    return (projects || []).flatMap((projeto) =>
      (projeto.projetosCamadas || [])
        .filter((camada) => !!camada && typeof camada === 'object')
        .map((camada) => ({
          ...camada,
          projeto,
          // O projeto OMI-RURAL não utiliza as flags do portal OVI. Elas são
          // normalizadas aqui para que as camadas possam ser exibidas no mapa.
          showOuv: true,
          publicoOuv: true,
        }))
    );
  }

  getCamadas(params: {
    showIcad?: boolean;
    showOuv?: boolean;
    publicoIcad?: boolean;
    publicoOuv?: boolean;
    noForms?: boolean;
    representacao?: string;
  }): Observable<Camada[]> {
    let httpParams = new HttpParams();

    if (params?.showIcad) {
      httpParams = httpParams.set('showIcad', 'true');
    }
    if (params?.showOuv) {
      httpParams = httpParams.set('showOuv', 'true');
    }
    if (params?.publicoIcad) {
      httpParams = httpParams.set('publicoIcad', 'true');
    }
    if (params?.publicoOuv) {
      httpParams = httpParams.set('publicoOuv', 'true');
    }
    if (params?.representacao) {
      httpParams = httpParams.set('representacao', params.representacao);
    }

    httpParams = httpParams.set('noForms', 'true');

    return this.http.get<Camada[]>(`${environment.apiUrl}camadas`, {
      params: httpParams,
    });
  }

  mapCamadasToMapLayers(camadas: Camada[]) {
    const projectsByJsonId = new Map<string, Projeto>();
    const projectsById = new Map<number, Projeto>();
    
    const hierarchyCache = new Map<number, Hierarchy[]>();

    (camadas || []).forEach((c) => {
      const p = c?.projeto as any;
      if (p && typeof p === 'object') {
        if (typeof p['@id'] === 'string') {
          projectsByJsonId.set(p['@id'], p as Projeto);
        }
        if (typeof p.id === 'number') {
          projectsById.set(p.id, p as Projeto);
        }
      }
    });

    const layers = (camadas || [])
      .filter((c) => !!c)
      .map((camada: Camada) => {
        let maxZoom = 0;
        const minZoom = 0;

        let projeto = camada?.projeto as any;
        if (typeof projeto === 'string') {
          projeto = projectsByJsonId.get(projeto);
        } else if (
          projeto &&
          typeof projeto === 'object' &&
          typeof projeto.id === 'number'
        ) {
          projeto = projectsById.get(projeto.id) || projeto;
        }

        let projectHierarchies: Hierarchy[] = hierarchyCache.get(projeto?.id) ?? [];
        if (projeto?.id) {
          if (hierarchyCache.has(projeto.id)) {
            projectHierarchies = hierarchyCache.get(projeto.id) ?? [];
          } else {
            try {
              const rawHierarquia = projeto.hierarquias;
              if (rawHierarquia && typeof rawHierarquia === 'string' && rawHierarquia !== 'null') {
                projectHierarchies = JSON.parse(rawHierarquia);
              }
            } catch (error) {
              console.warn(`Erro ao parsear hierarquia do projeto ${projeto.id}`, error);
              projectHierarchies = [];
            }
            hierarchyCache.set(projeto.id, projectHierarchies);
          }
        }

        const projectType =
          projeto?.tipoProjeto === 'ICAD'
            ? 'ICAD'
            : projeto?.tipoProjeto === 'PVGEO' ||
              projeto?.tipoProjeto === 'OUV' ||
              projeto?.tipoProjeto === 'OMI-RURAL'
            ? 'OUV'
            : 'WEBGEO';

        const baseObj = {
          representacao: camada.representacao,
          id: camada.id,
          name: camada.nome || '',
          geomType: camada.tipoGeometria,
          showOuv: camada.showOuv || false,
          showAvaliacao: camada.showAvaliacao || false,
          publicoOuv: camada.publicoOuv || false,
          showIcad: camada.showIcad || false,
          showPrevia: camada.showPrevia || false,
          publicoIcad: camada.publicoIcad || false,
          type: camada.representacao
            ? camada.representacao
            : (camada as any).nomeArquivo
            ? (camada as any).nomeArquivo
            : '',
          equation: camada.equacao || '',
          sldBody: camada.sldBody,
          sldBodyOuv: camada.sldBodyOuv,
          url: camada.url,
          tblNomeDb: camada.tblNomeDb,
          active: camada.ativo,
          additionalLayer: camada.adicional || false,
          projectName: projeto?.alias || projeto?.nome || 'Sem projeto',
          projectType,
          geoJSONStr: '',
          estiloWebgeo: camada.estiloWebgeo || '',
          estiloIcad: camada.estiloIcad || '',
          estiloConsultaPrevia: camada.estiloConsultaPrevia || '',
          estiloAvaliacao: camada.estiloAvaliacao || '',
          estiloOuv: camada.estiloOuv || '',
          maxZoom,
          minZoom,
          tipo: camada.tipo,
          hierarquias: projectHierarchies, 
          projectId: projeto?.id 
        };

        return of(baseObj);
      });

    return forkJoin(
      layers.filter((l) => !!l) as Observable<MapLayer | boolean>[]
    );
  }

  mapProjectsToMapLayersWithSideEffects(projects: Projeto[]) {
    // const ouv = projects.find(
    //   ({ tipoProjeto }) => tipoProjeto === TIPO_PROJETO.OUV
    // );
    const pvgeo = projects.find(
      ({ tipoProjeto }) =>
        tipoProjeto === TIPO_PROJETO.PVGEO ||
        tipoProjeto === TIPO_PROJETO.OMI_RURAL
    );
    const icad = projects.find(
      ({ tipoProjeto }) => tipoProjeto === TIPO_PROJETO.ICAD
    );

    // let filteredProjects = [ouv, pvgeo, icad] as Projeto[];

    let ids = {};

    // const layers = filteredProjects
    let layers = projects
      .filter((p) => !!p)
      .map((project) => {
        // const camadas = project.projetosCamadas as NotNull<
        //   typeof project.projetosCamadas
        // >;

        // const camadas2 = project.projetosCamadas.filter(
        //   (layer) => !layer.adicional
        // ) as Camada[];

        let camadas2 = project.projetosCamadas as Camada[];
        const comercialFactorLayer = project.importacoesTiff.find(
          (layer) => layer.nomeArquivo === LayerRepresentation.COMERCIAL_FACTOR
        );
        if (comercialFactorLayer) {
          camadas2 = [...camadas2, comercialFactorLayer];
        }
        // if (typeof firstLayer === 'string') {
        //   return null;
        // }

        // let restOfLayers = firstLayer.projeto?.projetosCamadas as Camada[];
        // restOfLayers?.shift();
        // restOfLayers = restOfLayers.filter((l) => typeof l !== 'string');
        // const camadas2 = [firstLayer, ...restOfLayers];

        return camadas2.map<Observable<MapLayer | boolean>>(
          (camada: Camada) => {
            // TODO zoom
            let maxZoom = 0;
            const minZoom = 0;

            const baseObj = {
              representacao: camada.representacao,
              id: camada.id,
              name: camada.nome || '',
              geomType: camada.tipoGeometria,
              showOuv: camada.showOuv || false,
              showAvaliacao: camada.showAvaliacao || false,
              publicoOuv: camada.publicoOuv || false,
              showIcad: camada.showIcad || false,
              showPrevia: camada.showPrevia || false,
              publicoIcad: camada.publicoIcad || false,
              type: camada.representacao
                ? camada.representacao
                : (camada as any).nomeArquivo
                ? (camada as any).nomeArquivo
                : '',
              equation: camada.equacao || '',
              sldBody: camada.sldBody,
              sldBodyOuv: camada.sldBodyOuv,
              url: camada.url,
              tblNomeDb: camada.tblNomeDb,
              active: camada.ativo,
              additionalLayer: camada.adicional || false,
              projectName: project.alias || project.nome || 'Sem projeto',
              projectType:
                project.tipoProjeto === 'ICAD'
                  ? 'ICAD'
                  : project.tipoProjeto === 'PVGEO' ||
                    project.tipoProjeto === 'OUV'
                  ? 'OUV'
                  : 'WEBGEO',
              geoJSONStr: '',
              estiloWebgeo: camada.estiloWebgeo || '',
              estiloIcad: camada.estiloIcad || '',
              estiloConsultaPrevia: camada.estiloConsultaPrevia || '',
              estiloAvaliacao: camada.estiloAvaliacao || '',
              estiloOuv: camada.estiloOuv || '',
              maxZoom,
              minZoom,
              tipo: camada.tipo,
            };
            // if (
            //   camada.representacao === LayerNames.SAMPLE_COLLAB_LAYER ||
            //   camada.representacao === LayerNames.SAVED_AVALIATIONS_LAYER
            // ) {
            //   return this.layersService.getGeoJSONwithValues(camada.id).pipe(
            //     map((gJSON) => ({
            //       ...baseObj,
            //       minZoom: 13,
            //       maxZoom: 25,
            //       geoJSONStr: JSON.stringify(gJSON),
            //     }))
            //   );
            // }

            // if (ids[camada.id]) {
            //   return of(false);
            // }

            // ids = {
            //   ...ids,
            //   [camada.id]: camada.id,
            // };

            return of(baseObj);
          }
        );
      });

    return forkJoin(
      layers
        .filter((l) => !!l)
        .reduce(
          (a, b) => [
            ...(a as Observable<MapLayer>[]),
            ...(b as Observable<MapLayer>[]),
          ],
          []
        )
    );
  }

  mapProjectsToIds(projects: Projeto[]) {
    const ouv = projects.find(
      ({ tipoProjeto }) => tipoProjeto === TIPO_PROJETO.OUV
    );

    const pvgeo = projects.find(
      ({ tipoProjeto }) =>
        tipoProjeto === TIPO_PROJETO.PVGEO ||
        tipoProjeto === TIPO_PROJETO.OMI_RURAL
    );

    const icad = projects.find(
      ({ tipoProjeto }) =>
        tipoProjeto === TIPO_PROJETO.ICAD ||
        tipoProjeto === (TIPO_PROJETO.ICAD_DEFAULT as TIPO_PROJETO)
    );

    if (icad?.tipoProjeto === (TIPO_PROJETO.ICAD_DEFAULT as TIPO_PROJETO)) {
      this.projectsFacade.dispatch(setIsICADDefault({ icadDefault: true }));
    }

    const zone = projects.find(
      ({ tipoProjeto }) => tipoProjeto === TIPO_PROJETO.ZONEAMENTO
    );

    if (ouv && pvgeo && icad) {
      this.projectsFacade.dispatch(
        setHasBothPortalModes({ hasBothPortalModes: true })
      );
    }

    return {
      ouvCollabId: ouv ? ouv.id : null,
      pvgeoId: pvgeo ? pvgeo.id : null,
      pvgeoGeomsQty:
        typeof pvgeo?.qtdGeometrias === 'number' ? pvgeo.qtdGeometrias : null,
      icadId: icad ? icad.id : null,
      icadGeomsQty:
        typeof icad?.qtdGeometrias === 'number' ? icad.qtdGeometrias : null,
      zoneId: zone ? zone.id : null,
      icadAPIAddr: icad ? icad['enderecoApi'] : null,
    };
  }

  mapCamadasToProjects(camadas: Camada[]): Projeto[] {
    const projectsById = new Map<number, Projeto>();
    const projectsByJsonId = new Map<string, Projeto>();

    (camadas || []).forEach((camada) => {
      const projeto = camada?.projeto as any;
      if (projeto && typeof projeto === 'object') {
        if (typeof projeto.id === 'number' && !projectsById.has(projeto.id)) {
          projectsById.set(projeto.id, projeto as Projeto);
        }
        if (
          typeof projeto['@id'] === 'string' &&
          !projectsByJsonId.has(projeto['@id'])
        ) {
          projectsByJsonId.set(projeto['@id'], projeto as Projeto);
        }
      }
    });

    (camadas || []).forEach((camada) => {
      const projetoRef = camada?.projeto as any;
      if (typeof projetoRef === 'string') {
        const found = projectsByJsonId.get(projetoRef);
        if (found && typeof found.id === 'number') {
          projectsById.set(found.id, found);
        }
      }
    });

    return Array.from(projectsById.values());
  }

  getUserProject(user: Usuario) {
    const { authToken, tnToken } = user;
    const httpOptions = {
      headers: new HttpHeaders()
        .set('X-AUTH-TOKEN', authToken)
        .set('X-TN-TOKEN', tnToken),
    };
    const url = `${environment.apiUrl}projetos/listar?ativo=ATIVOS`;
    return this.http.get(url, httpOptions);
  }

  getUserLayers(user: Usuario) {
    const { authToken, tnToken } = user;
    const httpOptions = {
      headers: new HttpHeaders()
        .set('X-AUTH-TOKEN', authToken)
        .set('X-TN-TOKEN', tnToken),
    };
    const url = `${environment.apiUrl}camadas`;
    return this.http.get(url, httpOptions);
  }

  getUserLayerById(user: Usuario, layerId: number) {
    const { authToken, tnToken } = user;
    const httpOptions = {
      headers: new HttpHeaders()
        .set('X-AUTH-TOKEN', authToken)
        .set('X-TN-TOKEN', tnToken),
    };
    const params = new HttpParams()
      .set('id_camadas', layerId.toString())
      .set('noForms', 'true');
    const url = `${environment.apiUrl}camadas`;
    return this.http
      .get(url, { ...httpOptions, params })
      .pipe(
        map((response: any) =>
          Array.isArray(response) ? response[0] : response
        )
      );
  }

  getUserPvgeoPermission(user: Usuario, pvgeoId: number, city?: string) {
    const { authToken, tnToken } = user;

    const normalizedCity = city?.toLowerCase?.() ?? '';

    const headers = new HttpHeaders()
      .set('X-AUTH-TOKEN', authToken)
      .set('X-TN-TOKEN', tnToken)
      .set('no-token', '')
      .set(
        'Application',
        CITIES_WITHOUT_APPLICATION_HEADER.has(normalizedCity)
          ? ''
          : 'Portal'
      );

    return this.http.get(`${environment.apiUrl}permissoes/${pvgeoId}`, {
      headers,
    });
  }

  getUserOuvCollabPermission(
    user: Usuario,
    ouvCollabId: number,
    city?: string
  ) {
    const { authToken, tnToken } = user;

    const normalizedCity = city?.toLowerCase?.() ?? '';

    const headers = new HttpHeaders()
      .set('X-AUTH-TOKEN', authToken)
      .set('X-TN-TOKEN', tnToken)
      .set('no-token', '')
      .set(
        'Application',
        CITIES_WITHOUT_APPLICATION_HEADER.has(normalizedCity)
          ? ''
          : 'Portal'
      );

    return this.http.get(`${environment.apiUrl}permissoes/${ouvCollabId}`, {
      headers,
    });
  }

  getPublicCompanyInfo() {
    return this.http
      .get(`${environment.apiUrl}empresas`)
      .pipe(debounceTime(500));
  }

  sendColaborateEmail(mensagem: string, emailToSend: string) {
    const url = `${environment.apiUrl}email/personalizado`;
    return this.http.post(url, {
      assunto: 'Visitante público quer se tornar um colaborador',
      mensagem,
    });
  }

  getAuthToken(user: Usuario) {
    const { authToken } = user;
    return authToken;
  }

  getTnToken() {
    return localStorage.getItem('X-TN-TOKEN');
  }
}
